/**
 * AdmissionApprovalsPage (Phase-2, Module 4 — admin/coordinator side)
 *
 * Lists all IPD admissions and exposes the full 8-status workflow actions:
 * review → approve → admit → start treatment → discharge → close, plus reject.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, IconButton, Tooltip, Chip, Stack } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { LocalHospital as HospIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { PageScaffold, StatusBadge, ConfirmDialog } from '../../components/common';
import { AdmissionApi } from '../../services/phase2Api';
import { ADMISSION_TYPES, labelFor } from '../../utils/phase2Constants';
import type { HospitalAdmission } from '../../types/phase2';

const AdmissionApprovalsPage: React.FC = () => {
  const [rows, setRows] = useState<HospitalAdmission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const page = await AdmissionApi.listAll({ page: 0, size: 100 });
      setRows(page?.content || []);
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to load admissions'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<HospitalAdmission>, label: string) => {
    try { await fn(); load(); }
    catch (e: any) { setError(e?.response?.data?.message || `${label} failed`); }
  };

  const handleReject = async (reason?: string) => {
    if (rejectId == null || !reason) return;
    try { await AdmissionApi.reject(rejectId, reason); setRejectId(null); load(); }
    catch (e: any) { setError(e?.response?.data?.message || 'Reject failed'); }
  };

  const columns: GridColDef[] = [
    { field: 'admissionNumber', headerName: 'Admission No.', width: 140 },
    { field: 'officerName', headerName: 'Officer', width: 150, renderCell: (p) => p.value || '—' },
    { field: 'hospitalName', headerName: 'Hospital', flex: 1.2, minWidth: 150 },
    { field: 'admissionType', headerName: 'Type', width: 110, renderCell: (p) => <Chip size="small" label={labelFor(ADMISSION_TYPES, p.value)} variant="outlined" /> },
    { field: 'status', headerName: 'Status', width: 150, renderCell: (p) => <StatusBadge status={p.value} size="small" /> },
    {
      field: 'actions', headerName: 'Actions', width: 360, sortable: false,
      renderCell: (p) => {
        const s = p.row.status as string;
        const id = p.row.id as number;
        const btn = (label: string, fn: () => Promise<HospitalAdmission>, color?: 'primary' | 'success' | 'error' | 'warning' | 'info', disabled = false) => (
          <Button size="small" variant={color === 'error' ? 'outlined' : 'contained'} color={color || 'primary'}
            disabled={disabled} onClick={() => act(fn, label)} sx={{ textTransform: 'none', mr: 0.5, mb: 0.5 }}>{label}</Button>
        );
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {s === 'REQUESTED' && btn('Review', () => AdmissionApi.review(id), 'info')}
            {s === 'UNDER_REVIEW' && btn('Approve', () => AdmissionApi.approve(id), 'success')}
            {(s === 'REQUESTED' || s === 'UNDER_REVIEW') && <Button size="small" variant="outlined" color="error" onClick={() => setRejectId(id)} sx={{ textTransform: 'none', mr: 0.5 }}>Reject</Button>}
            {s === 'APPROVED' && btn('Admit', () => AdmissionApi.admit(id), 'primary')}
            {s === 'ADMITTED' && btn('Start Treatment', () => AdmissionApi.startTreatment(id), 'warning')}
            {s === 'UNDER_TREATMENT' && btn('Discharge', () => AdmissionApi.discharge(id), 'success')}
            {s === 'DISCHARGED' && btn('Close', () => AdmissionApi.close(id), 'primary')}
            <Tooltip title="View"><IconButton size="small" href={`/admin/admissions/${id}`}><ViewIcon fontSize="small" /></IconButton></Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <PageScaffold title="Admission Approvals" subtitle="Manage IPD admission workflow" icon={<HospIcon />}
      loading={loading && rows.length === 0} error={error} onDismissError={() => setError(null)}>
      <Box sx={{ height: 560, bgcolor: '#fff', borderRadius: 3, border: '1px solid #E5E7EB' }}>
        <DataGrid rows={rows} columns={columns} getRowId={(r) => r.id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
      </Box>
      <ConfirmDialog open={rejectId != null} title="Reject Admission" message="Provide a reason for rejecting this admission request."
        confirmLabel="Reject" requireReason reasonLabel="Rejection reason" onConfirm={handleReject} onClose={() => setRejectId(null)} />
    </PageScaffold>
  );
};

export default AdmissionApprovalsPage;